/**
 * Playlist Partitioning Utility
 *
 * Separates critical playlists from regular playlists based on:
 * - Priority level (5 = critical)
 * - Schedule type (PrayerBased)
 *
 * Critical playlists represent prayer-based critical content and pause
 * regular slideshow content except prayer countdown and salah screen blank.
 */

import { Playlist, ScheduleType } from "../types/index";

// ============================================================================
// INTERFACES
// ============================================================================

export interface PartitionedPlaylists {
  /** The highest priority critical playlist (only one can be active) */
  critical: Playlist | null;
  /** All non-critical playlists for regular slideshow */
  regular: Playlist[];
}

export interface CriticalPlaylistCriteria {
  /** Priority level that qualifies as critical */
  priorityLevel: number;
  /** Required schedule types for critical playlists */
  allowedScheduleTypes: ScheduleType[];
  /** Whether to require prayer-based scheduling */
  requirePrayerBased: boolean;
}

// ============================================================================
// DEFAULT CRITERIA
// ============================================================================

export const DEFAULT_CRITICAL_CRITERIA: CriticalPlaylistCriteria = {
  priorityLevel: 5,
  allowedScheduleTypes: [ScheduleType.PrayerBased],
  requirePrayerBased: true,
};

// ============================================================================
// PARTITIONING FUNCTIONS
// ============================================================================

/**
 * Determines if a playlist meets critical criteria
 */
export function isCriticalPlaylist(
  playlist: Playlist,
  criteria: CriticalPlaylistCriteria = DEFAULT_CRITICAL_CRITERIA,
): boolean {
  // Check priority level
  if (playlist.priority !== criteria.priorityLevel) {
    return false;
  }

  // Check schedule type requirement
  if (criteria.requirePrayerBased) {
    // Handle both enum and string values for schedule type
    const scheduleType =
      typeof playlist.scheduleType === "string"
        ? ScheduleType[playlist.scheduleType as keyof typeof ScheduleType]
        : playlist.scheduleType;

    return criteria.allowedScheduleTypes.includes(scheduleType);
  }

  return true;
}

/**
 * Partitions active playlists into critical and regular categories
 */
export function partitionPlaylists(
  activePlaylists: Playlist[],
  criteria: CriticalPlaylistCriteria = DEFAULT_CRITICAL_CRITERIA,
): PartitionedPlaylists {
  if (!activePlaylists.length) {
    return {
      critical: null,
      regular: [],
    };
  }

  // Find all critical playlists
  const criticalPlaylists = activePlaylists.filter((playlist) =>
    isCriticalPlaylist(playlist, criteria),
  );

  // Select highest priority critical playlist (should only be one with priority 5)
  // If multiple exist, take the first one found
  const critical = criticalPlaylists.length > 0 ? criticalPlaylists[0] : null;

  // All other playlists are regular
  const regular = critical
    ? activePlaylists.filter((playlist) => playlist.id !== critical.id)
    : activePlaylists;

  return {
    critical,
    regular,
  };
}

/**
 * Gets summary information about partitioned playlists
 */
export function getPartitionSummary(partitioned: PartitionedPlaylists) {
  return {
    hasCritical: partitioned.critical !== null,
    criticalId: partitioned.critical?.id || null,
    criticalName: partitioned.critical?.name || null,
    regularCount: partitioned.regular.length,
    regularIds: partitioned.regular.map((p) => p.id),
  };
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validates that critical playlist configuration is correct
 */
export function validateCriticalPlaylist(playlist: Playlist): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check required fields
  if (!playlist.id) {
    errors.push("Critical playlist must have an ID");
  }

  if (!playlist.name) {
    errors.push("Critical playlist must have a name");
  }

  if (playlist.priority !== 5) {
    errors.push("Critical playlist must have priority level 5");
  }

  // Check schedule type for legacy critical behavior
  const scheduleType =
    typeof playlist.scheduleType === "string"
      ? ScheduleType[playlist.scheduleType as keyof typeof ScheduleType]
      : playlist.scheduleType;

  if (scheduleType !== ScheduleType.PrayerBased) {
    errors.push("Critical playlist must be prayer-based");
  }

  // Check if it has items
  if (!playlist.items || playlist.items.length === 0) {
    errors.push("Critical playlist must have at least one item");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// ============================================================================
// DEBUGGING HELPERS
// ============================================================================

/**
 * Logs partition information for debugging
 */
export function logPartitionDetails(
  activePlaylists: Playlist[],
  partitioned: PartitionedPlaylists,
  context?: string,
) {
  const summary = getPartitionSummary(partitioned);

  console.log(`🎭 Playlist Partition ${context ? `(${context})` : ""}:`, {
    input: {
      totalActive: activePlaylists.length,
      activeIds: activePlaylists.map((p) => p.id),
    },
    output: summary,
    details: {
      critical: partitioned.critical
        ? {
            id: partitioned.critical.id,
            name: partitioned.critical.name,
            priority: partitioned.critical.priority,
            scheduleType: partitioned.critical.scheduleType,
          }
        : null,
      regularNames: partitioned.regular.map((p) => p.name),
    },
  });
}
